import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CreateAxiosDefaults,
} from 'axios';
import {
  getGoogleClientId,
  getGoogleSessionId,
  getUtmSuperProperties,
} from '@monorepo/client-common';
import { HttpStatus } from '@monorepo/client-types';
import { getDescopeSessionToken } from '@monorepo/hub-common';
import {
  AdminTokenHeaderKey,
  CommonErrors,
  FeatureFlagAccessTokenHeaderKey,
  FeatureFlagAccessTokenKey,
  IDP_PROVIDER,
  IdpProviderKey,
  IsLoggedOutKey,
  logoutUrl,
  refreshTokenUrl,
  TokenSyncStatusHeaderKey,
  TokenSyncStatusKey,
} from '@monorepo/types';
import { EnvService } from './env-service';
import { ERROR_MESSAGES, UIErrorService } from './error-service';

export interface HttpServiceConfig {
  baseURL?: string;
}

/**
 * HttpService class provides a wrapper around Axios for making HTTP requests.
 * It supports GET, POST, PUT, PATCH, and DELETE methods, and includes token
 * management for authorization. The service handles API errors by wrapping
 * them in an ApiError object and supports automatic token refresh on 401
 * Unauthorized errors, with a configurable retry mechanism.
 * @property {string} token - The authorization token used for requests.
 * @property {AxiosInstance} axiosInstance - The Axios instance used for HTTP requests.
 * @property {number} API_TIMEOUT - Timeout for API requests in milliseconds.
 * @property {object} API_HEADERS - Default headers for API requests.
 * @function getAxiosInstance - Returns the Axios instance.
 * @function get - Performs a GET request.
 * @function post - Performs a POST request.
 * @function put - Performs a PUT request.
 * @function patch - Performs a PATCH request.
 * @function delete - Performs a DELETE request.
 */
export default class HttpService {
  private readonly axiosInstance: AxiosInstance;
  private readonly API_TIMEOUT = 30000;
  private readonly API_HEADERS = {
    'Content-Type': 'application/json',
  };

  private isRefreshing = false;
  private failedQueue: Array<() => void> = [];
  private abortController!: AbortController;

  private readonly allowUrls = [refreshTokenUrl, logoutUrl];

  private logout?: () => void;

  private refreshToken?: () => Promise<void>;

  public setActions(
    logout: () => Promise<void>,
    refreshToken: () => Promise<void>,
    adminToken?: string,
  ) {
    this.logout = logout;
    this.refreshToken = refreshToken;

    // Always set or clear the admin token header
    // This ensures the header is properly managed during impersonation
    if (adminToken) {
      this.axiosInstance.defaults.headers[AdminTokenHeaderKey] = adminToken;
    } else {
      // Explicitly remove the header if adminToken is not provided
      delete this.axiosInstance.defaults.headers[AdminTokenHeaderKey];
    }
  }

  /**
   * Explicitly removes the X-Admin-Token header from axios defaults.
   * Must be called when impersonation ends (e.g. user navigates away from
   * impersonation URL) since setActions is only invoked at startup.
   * Otherwise the stale header persists and credentials leak.
   */
  public clearAdminToken() {
    delete this.axiosInstance.defaults.headers[AdminTokenHeaderKey];
  }

  /**
   * Sets a fallback Authorization header for flows where the Descope SDK
   * cannot provide the token yet (e.g. quick-purchase anonymous session).
   * The interceptor clears this default once the SDK has the token.
   */
  public setAuthorizationHeader(token: string) {
    this.axiosInstance.defaults.headers['Authorization'] = `Bearer ${token}`;
  }

  private setSignal() {
    this.abortController = new AbortController();
    this.axiosInstance.defaults.signal = this.abortController.signal;
  }

  /**
   *
   */
  constructor({ baseURL }: HttpServiceConfig) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: this.API_TIMEOUT,
      headers: { ...this.API_HEADERS },
      withCredentials: true,
    } as CreateAxiosDefaults);

    this.setSignal();

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === HttpStatus.UNAUTHORIZED) {
          if (
            error.response?.data?.message ===
              CommonErrors.FAILED_TOKEN_EXPIRED &&
            !error.config._retry
          ) {
            // Skip token refresh if user is logged out
            // Descope users: SDK/widget refreshes tokens proactively, so tokens never expire
            // Logged out users: no refresh needed
            // Otherwise (Cognito/OTP): attempt refresh
            const isLoggedOut = localStorage.getItem(IsLoggedOutKey) === 'true';

            // Reject if: explicitly logged out
            if (isLoggedOut) {
              return Promise.reject(error);
            }

            return this.handleTokenExpiry(error);
          }

          if (
            [
              CommonErrors.FAILED_CREDENTIALS,
              CommonErrors.FAILED_PASSWORD,
            ].includes(error.response?.data?.message)
          ) {
            return Promise.reject(this.handleUIError(error));
          }

          // If the error is due to no auth token, we don't need to redirect on the login page.
          // This is useful for cases where the user is forced to log out due to a duplicated user issue.
          // Redirect will be to the forgot password page with the error message.
          // Reference:
          // ...auth-service/auth-service.service.ts
          // ...controllers/user/user.controller.ts
          if (error.response?.data?.message === CommonErrors.NO_AUTH_TOKEN) {
            localStorage.setItem(IsLoggedOutKey, 'true');
            return Promise.reject(
              new Error('No auth token provided due duplicate user.'),
            );
          }

          return this.logout?.();
        }

        return Promise.reject(this.handleUIError(error));
      },
    );

    this.axiosInstance.interceptors.request.use(
      (config: any) => {
        // Add Google Analytics headers to all requests
        const googleClientId = getGoogleClientId();
        const sessionId = getGoogleSessionId(EnvService.gaId);

        if (googleClientId) {
          config.headers['X-Ga-Client-Id'] = googleClientId;
        }
        if (sessionId) {
          config.headers['X-Session-Id'] = sessionId;
        }

        // Add brand header for backend analytics segmentation
        config.headers['X-Brand'] = EnvService.application;
        // Add IDP provider header for backend strategy selection
        // This allows existing users to continue using their auth method even if feature flag changes
        const idpProvider = localStorage.getItem(IdpProviderKey);
        if (idpProvider) {
          config.headers['x-idp-provider'] = idpProvider;
        }

        // Add Descope session token to Authorization header only if IDP provider is Descope
        // This allows mobile/alternate-origin clients to authenticate when cookies don't work
        // The DS cookie is only present when Descope is enabled and user is authenticated
        if (idpProvider === IDP_PROVIDER.DESCOPE) {
          const descopeSessionToken = getDescopeSessionToken();
          if (descopeSessionToken) {
            config.headers['Authorization'] = `Bearer ${descopeSessionToken}`;
            // SDK has the token — remove any manually-injected default so it
            // doesn't linger after the quick-purchase bootstrap is complete.
            delete this.axiosInstance.defaults.headers['Authorization'];
          }
          // Add token sync status header for backend to determine if token needs MongoDB lookup
          // This allows backend to know if the Descope token attributes are in sync
          const tokenSyncStatus = localStorage.getItem(TokenSyncStatusKey);
          if (tokenSyncStatus) {
            config.headers[TokenSyncStatusHeaderKey] = tokenSyncStatus;
          }
        }

        // Add feature flag access token header (from URL query param ?accessToken=...)
        // This allows backend to check feature flags using the accessToken attribute
        // Works for both Descope and OTP flows
        const accessToken = localStorage.getItem(FeatureFlagAccessTokenKey);
        if (accessToken) {
          config.headers[FeatureFlagAccessTokenHeaderKey] = accessToken;
        }

        // Add language header for backend event alignment
        const hubLanguage = localStorage.getItem('i18nextLng') || 'en';
        config.headers['X-Hub-Language'] = hubLanguage.toLowerCase();

        // Add UTM headers for backend Mixpanel event tracking
        const utmProperties = getUtmSuperProperties();
        if (utmProperties.utm_source) {
          config.headers['X-UTM-Source'] = utmProperties.utm_source;
        }
        if (utmProperties.utm_medium) {
          config.headers['X-UTM-Medium'] = utmProperties.utm_medium;
        }
        if (utmProperties.utm_campaign) {
          config.headers['X-UTM-Campaign'] = utmProperties.utm_campaign;
        }

        // Add feature flag access token header for backend feature flag evaluation
        // This allows backend to check feature flags using the same accessToken as frontend
        // The accessToken is extracted from URL query parameter (?accessToken=xxx) and stored in localStorage
        const featureFlagAccessToken = localStorage.getItem(
          FeatureFlagAccessTokenKey,
        );
        if (featureFlagAccessToken) {
          config.headers[FeatureFlagAccessTokenHeaderKey] =
            featureFlagAccessToken;
        }

        return this.isRefreshing && !this.allowUrls.includes(config.url || '')
          ? this.deferRequest(config)
          : config;
      },
      (error) => Promise.reject(error),
    );
  }

  private deferRequest(config: any) {
    return new Promise((resolve, reject) => {
      this.failedQueue.push(() => {
        this.axiosInstance(config).then(resolve).catch(reject);
      });
    });
  }

  /**
   *
   */
  getAxiosInstance() {
    return this.axiosInstance;
  }

  private handleTokenExpiry(error: any) {
    const originalRequest = error.config;
    originalRequest._retry = true;

    const handleError = (e: any) => {
      this.abortController.abort('Token refresh failed');
      console.error('Token refresh failed,', e);
      this.setSignal();
      this.logout?.();
    };

    const clearQueue = () => {
      const queue = [...this.failedQueue];
      this.failedQueue = [];
      queue.forEach((req) => req());
    };

    if (!this.isRefreshing) {
      this.isRefreshing = true;

      if (this.refreshToken) {
        this.refreshToken()
          .catch(handleError)
          .finally(() => {
            this.isRefreshing = false;
            clearQueue();
          });
      } else {
        handleError('No refresh token function provided');
      }
    }

    return this.deferRequest(originalRequest);
  }

  private handleUIError(error: any) {
    if (error.response) {
      const { status, data } = error.response;
      return new UIErrorService({
        message: data.message,
        status,
        data,
      });
    } else if (error.request) {
      return new UIErrorService({
        message: ERROR_MESSAGES.NO_RESPONSE,
      });
    } else {
      return new UIErrorService({
        message: error.message,
      });
    }
  }

  private handleResponse<T>(response: AxiosResponse<T>): T {
    return response.data;
  }

  /**
   *
   */
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.get<T>(url, config).then(this.handleResponse);
  }

  /**
   *
   */
  public async post<T, R>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.axiosInstance
      .post<R>(url, data, config)
      .then(this.handleResponse);
  }

  /**
   *
   */
  public async put<T, R>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.axiosInstance
      .put<R>(url, data, config)
      .then(this.handleResponse);
  }

  /**
   *
   */
  public async patch<T, R>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.axiosInstance
      .patch<R>(url, data, config)
      .then(this.handleResponse);
  }

  /**
   *
   */
  public async delete<R>(url: string, config?: AxiosRequestConfig): Promise<R> {
    return this.axiosInstance.delete<R>(url, config).then(this.handleResponse);
  }
}
